import React from "react";
import { AlertTriangle, Building2, ArrowRight } from "lucide-react";
import ConfirmDialog from "components/ConfirmDialog";
import { buildSubmissionSummary } from "utilities/midApplicationSubmission";

/**
 * SOUND-643 — confirmation gate for MID application creation.
 *
 * This action creates an application PER SELECTED PROCESSOR in one click, and
 * those records are the trigger for outbound processor submissions. It ran
 * straight off the button with no confirmation, and two silent fallbacks decided
 * the destination: an unresolvable processorId became "Unknown Processor", and a
 * company with no legal name became "Unknown Company".
 *
 * The dialog answers "where is this actually going" before the click rather than
 * after. Summary building and warning detection are in
 * utilities/midApplicationSubmission.js, which has tests.
 */
export default function SubmitConfirmation({
  open,
  onConfirm,
  onCancel,
  submitting,
  processorIds,
  merchants,
  registration,
  selectedDbaId,
}) {
  const summary = buildSubmissionSummary({
    processorIds,
    merchants,
    registration,
    selectedDbaId,
  });

  const plural = summary.count === 1 ? "application" : "applications";

  return (
    <ConfirmDialog
      open={open}
      onConfirm={onConfirm}
      onCancel={onCancel}
      title={`Create ${summary.count} MID ${plural}?`}
      confirmLabel={submitting ? "Creating…" : `Create ${summary.count} ${plural}`}
    >
      <div className="space-y-4">
        {/* Who these are for. Rendered first because a MID application against
            the wrong entity is the expensive mistake, not the wrong count. */}
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <div className="flex items-start gap-2">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground">
                {summary.companyName}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {summary.isParentCorp
                  ? "Parent corporation — no DBA selected"
                  : "DBA"}
              </div>
            </div>
          </div>
        </div>

        {/* Where each one is going. */}
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Submitting to
          </div>
          <ul className="divide-y divide-border/60 rounded-lg border border-border">
            {summary.rows.map((r, i) => (
              <li
                key={`${r.processorId}-${i}`}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span
                    className={
                      r.resolved
                        ? "truncate text-sm text-foreground"
                        : "truncate text-sm text-warning"
                    }
                  >
                    {r.processorName}
                  </span>
                </span>
                {!r.resolved && (
                  <span className="shrink-0 text-[11px] uppercase tracking-wide text-warning">
                    unidentified
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {summary.warnings.length > 0 && (
          <div className="space-y-1.5 rounded-lg border border-warning/30 bg-warning/5 p-3">
            {summary.warnings.map((w) => (
              <div key={w.code} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span className="text-xs text-warning">{w.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}
